import type { Progress } from '@dcs/platform/ports';
import type { TheoryClassCounts } from 'virtual:theory-counts';
import { slugOf, type HomeClass } from './class-store';

/** The latest saved theory attempt, as far as the home page needs it. */
export interface LatestAttempt {
  readonly licenseClass: string;
  readonly passed: boolean;
  readonly score: number;
  readonly maxScore: number;
  /** Epoch ms of `finishedAt`. */
  readonly finishedAt: number;
}

/** What the big "Tiếp tục" button does. `to` is the link (path + query). */
export type ContinueAction =
  | { readonly kind: 'start'; readonly to: string }
  | {
      readonly kind: 'chapter';
      readonly chapter: number;
      /** 1-based question of the chapter to open. */
      readonly position: number;
      readonly total: number;
      readonly to: string;
    }
  | { readonly kind: 'wrong'; readonly count: number; readonly to: string }
  | { readonly kind: 'retake'; readonly to: string }
  | { readonly kind: 'exam'; readonly to: string };

const theory = (licenseClass: HomeClass, query: string) =>
  `/ly-thuyet?hang=${slugOf(licenseClass)}${query}`;

const chapterLink = (licenseClass: HomeClass, chapter: number, position: number) =>
  theory(
    licenseClass,
    `&ontap=${String(chapter)}${position > 1 ? `&cau=${String(position)}` : ''}`,
  );

/** Question number of an id (`q183` → 183); `null` for any other shape. */
export function questionNumber(id: string): number | null {
  const match = /^q(\d+)$/.exec(id);
  return match ? Number(match[1]) : null;
}

/** The newest answer that carries a time (compacted entries have `at: 0`). */
function lastAnswer(progress: Progress): { id: string; at: number } | null {
  let best: { id: string; at: number } | null = null;
  for (const [id, entry] of Object.entries(progress.answered)) {
    if (entry.at > 0 && (best === null || entry.at > best.at)) best = { id, at: entry.at };
  }
  return best;
}

/**
 * The most recent unfinished thing of the picked class, from the local progress and (signed in)
 * the latest saved theory attempt:
 *  - a failed exam of this class newer than the last practice answer: "Làm lại đề hạng B";
 *  - the last answer in a chapter that has more questions: the next one ("Tiếp tục: Chương 2 ·
 *    câu 4/25"), opened at that card by `&cau=`;
 *  - its chapter done: the wrong answers if any ("Ôn lại 7 câu sai"), else the next chapter, else a
 *    mock exam of the class;
 *  - nothing yet: "Bắt đầu ôn lý thuyết" (chapter 1).
 * Pure: the caller passes the data, nothing here reads a clock or storage.
 */
export function continueAction(
  licenseClass: HomeClass,
  counts: TheoryClassCounts,
  progress: Progress,
  attempt: LatestAttempt | null,
): ContinueAction {
  const chapters = counts.byChapter;
  const first = chapters[0]?.chapter ?? 1;
  const wrong = progress.wrong.length;
  const last = lastAnswer(progress);

  const failed = attempt !== null && !attempt.passed && attempt.licenseClass === licenseClass;
  if (failed && (last === null || attempt.finishedAt >= last.at)) {
    return { kind: 'retake', to: theory(licenseClass, '') };
  }

  const number = last === null ? null : questionNumber(last.id);
  const at =
    number === null
      ? -1
      : chapters.findIndex((c) => number >= c.firstNumber && number < c.firstNumber + c.questions);
  const chapter = chapters[at];
  if (number !== null && chapter !== undefined) {
    const position = number - chapter.firstNumber + 1;
    if (position < chapter.questions) {
      return {
        kind: 'chapter',
        chapter: chapter.chapter,
        position: position + 1,
        total: chapter.questions,
        to: chapterLink(licenseClass, chapter.chapter, position + 1),
      };
    }
    if (wrong > 0) return { kind: 'wrong', count: wrong, to: theory(licenseClass, '&ontap=sai') };
    const next = chapters[at + 1];
    if (next !== undefined) {
      return {
        kind: 'chapter',
        chapter: next.chapter,
        position: 1,
        total: next.questions,
        to: chapterLink(licenseClass, next.chapter, 1),
      };
    }
    return { kind: 'exam', to: theory(licenseClass, '') };
  }

  if (wrong > 0) return { kind: 'wrong', count: wrong, to: theory(licenseClass, '&ontap=sai') };
  return { kind: 'start', to: theory(licenseClass, `&ontap=${String(first)}`) };
}

/** Theory progress (A-5): questions whose latest answer is right, over the class's total. */
export function theoryProgress(
  progress: Progress,
  total: number,
): { correct: number; total: number; percent: number } {
  const correct = Object.values(progress.answered).filter((entry) => entry.correct).length;
  const percent = total > 0 ? Math.floor((Math.min(correct, total) / total) * 100) : 0;
  return { correct, total, percent };
}
